import type {ReactNode} from 'react';
import clsx from 'clsx';

import {ArrowLink} from '@/modules/finkn/components/arrow-link';
import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';

type Filter = 'all' | 'forsikring' | 'bank' | 'inkasso';

interface Props extends ContentBlockContainerProps {
	heading?: string;
	anchorHeadings?: boolean;
	description?: ReactNode;
	filter?: Filter;
	insideToc?: boolean;
}

export const companiesListLinksDescriptionPortableTextOverrides: PortableTextOverrides =
	{
		p: 'text-xl',
	};

export const CompaniesListLinks = ({
	heading,
	anchorHeadings,
	options,
	description,
	filter,
	insideToc,
}: Props) => {
	const insuranceHref = `${internalLinkToHref('companies-frontpage')}?fieldOfArea=forsikring`;
	const bankingHref = `${internalLinkToHref('companies-frontpage')}?fieldOfArea=bank`;
	const debtCollectionHref = `${internalLinkToHref('companies-frontpage')}?fieldOfArea=inkasso`;

	const renderLinks = (filter?: Filter) => {
		switch (filter) {
			case 'forsikring':
				return (
					<>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink className="py-5" href={insuranceHref}>
								Foretak innen forsikring
							</ArrowLink>
						</li>
					</>
				);
			case 'bank':
				return (
					<>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink className="py-5" href={bankingHref}>
								Foretak innen bank og finans
							</ArrowLink>
						</li>
					</>
				);
			case 'inkasso':
				return (
					<>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink
								className="py-5"
								href={debtCollectionHref}
							>
								Foretak innen inkasso
							</ArrowLink>
						</li>
					</>
				);
			default:
				return (
					<>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink className="py-5" href={insuranceHref}>
								Foretak innen forsikring
							</ArrowLink>
						</li>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink className="py-5" href={bankingHref}>
								Foretak innen bank og finans
							</ArrowLink>
						</li>
						<li
							className={clsx(
								'border-b',
								'border-grey-border',
								'first:border-t',
							)}
						>
							<ArrowLink
								className="py-5"
								href={debtCollectionHref}
							>
								Foretak innen inkasso
							</ArrowLink>
						</li>
					</>
				);
		}
	};

	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<div
					className={clsx(
						'grid',
						'@2xl:grid-cols-12',
						'@2xl:gap-10',
						'relative',
					)}
				>
					<div className="@2xl:col-span-4">
						<div
							className={clsx(
								'@2xl:sticky',
								'@2xl:top-8',
								'mb-9',
								'@2xl:mb-0',
							)}
						>
							<ContentBlockHeading
								heading={heading}
								anchorHeading={anchorHeadings}
							>
								{description}
							</ContentBlockHeading>
						</div>
					</div>
					<ul className="@2xl:col-span-7 @2xl:-col-end-1">
						{renderLinks(filter)}
					</ul>
				</div>
			</div>
		</ContentBlockContainer>
	);
};
